// =====================================================================
// WeddingOS – /join/:token  (UPDATED)
// =====================================================================
// Erkennt automatisch ob der Token ein:
//   A) Gast-Token (invite_token)  → persönlicher Link, Name vorausgefüllt
//   B) Tisch-Token (table_token)  → QR-Code Tischkarte
//
// Für B) wenn Gast bereits eingeloggt ist:
//   → Tisch Check-in Flow (Variante A: automatischer Check-in)
//   → Gast wird dem Tisch zugewiesen, kein Name nötig
//
// Für B) wenn Gast nicht eingeloggt ist:
//   → Walk-in Flow (Variante B: Name eingeben, wie bisher)
// =====================================================================

import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../../services/supabase";
import { saveGuestSession, loadGuestSession, greetName, tableDisplay } from "../../lib/guestSession";
import "../../styles/theme.css";

// ── Token-Typ erkennen ────────────────────────────────────────────────
// Versucht zuerst Gast-Token, dann Tisch-Token
async function resolveToken(token) {
  // 1. Gast-Token versuchen
  const { data: guestData } = await supabase.rpc("get_guest_info", { p_token: token });
  const guest = Array.isArray(guestData) ? guestData[0] : guestData;
  if (guest?.guest_id) return { type: "guest", data: guest };

  // 2. Tisch-Token versuchen
  const { data: tableData } = await supabase.rpc("get_table_info", { p_table_token: token });
  const table = Array.isArray(tableData) ? tableData[0] : tableData;
  if (table?.wedding_id) return { type: "table", data: table };

  throw new Error("invalid_token");
}

// ── Check-in RPC ──────────────────────────────────────────────────────
async function doTableCheckin(guestId, tableToken) {
  const { data, error } = await supabase.rpc("table_checkin", {
    p_guest_id:    guestId,
    p_table_token: tableToken,
  });
  if (error) throw error;
  return Array.isArray(data) ? data[0] : data;
}

// ── Table Join (Walk-in) ──────────────────────────────────────────────
async function doTableJoin(tableToken, guestName) {
  const { data, error } = await supabase.rpc("table_join", {
    p_table_token: tableToken,
    p_guest_name:  guestName,
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error("join_failed");
  return row;
}

// =====================================================================
export default function Join() {
  const { tableToken: urlToken } = useParams();
  const navigate                 = useNavigate();

  const [phase,      setPhase]      = useState("loading"); // loading | guest | checkin | walkin | done | error
  const [resolved,   setResolved]   = useState(null);      // { type, data }
  const [error,      setError]      = useState(null);
  const [name,       setName]       = useState("");
  const [nameError,  setNameError]  = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!urlToken) { setError("Kein Token in der URL."); setPhase("error"); return; }
    init();
  }, [urlToken]);

  useEffect(() => {
    if (phase === "walkin" || phase === "guest") inputRef.current?.focus();
  }, [phase]);

  async function init() {
    const session = loadGuestSession();

    try {
      const result = await resolveToken(urlToken);
      setResolved(result);

      // ── A: Gast-Token ────────────────────────────────────────────
      if (result.type === "guest") {
        const g = result.data;

        // Bereits diese Session? → direkt weiterleiten
        if (session?.guest_id === g.guest_id) {
          navigate("/guest", { replace: true });
          return;
        }

        // Session speichern + zur Feier (Name ist bereits bekannt)
        saveGuestSession({
          tableToken:   urlToken,
          guest_id:     g.guest_id,
          guest_name:   g.guest_name,
          nickname:     g.nickname,
          table_number: g.table_number,
          table_label:  g.table_label,
          wedding_id:   g.wedding_id,
          wedding_name: g.wedding_name,
          event_date:   g.event_date,
          location:     g.location,
        });

        // Kurz den personalisierten Willkommens-Screen zeigen
        setPhase("guest");
        return;
      }

      // ── B: Tisch-Token ───────────────────────────────────────────
      if (result.type === "table") {
        // Bereits eingeloggt (Gast hat persönlichen Link genutzt)?
        // → Tisch Check-in (Variante A)
        if (session?.guest_id && session?.wedding_id === result.data.wedding_id) {
          setPhase("checkin");
          // Automatisch einchecken
          await performCheckin(session, urlToken);
          return;
        }

        // Bereits mit diesem Tisch-Token eingeloggt?
        if (session?.tableToken === urlToken && session?.guest_id) {
          navigate("/guest", { replace: true });
          return;
        }

        // Walk-in: Name eingeben (Variante B)
        setPhase("walkin");
      }

    } catch (err) {
      setError(
        err.message?.includes("invalid_token")
          ? "Dieser Link ist nicht gültig oder abgelaufen."
          : "Verbindungsfehler. Bitte nochmal versuchen."
      );
      setPhase("error");
    }
  }

  // ── Tisch Check-in durchführen ────────────────────────────────────
  async function performCheckin(session, token) {
    try {
      const result = await doTableCheckin(session.guest_id, token);
      // Session mit neuem Tisch aktualisieren
      saveGuestSession({
        ...session,
        table_number: result.table_number,
        table_label:  result.table_label,
        checked_in:   true,
      });
      setPhase("done");
      setTimeout(() => navigate("/guest", { replace: true }), 1800);
    } catch {
      // Check-in fehlgeschlagen → trotzdem weiterleiten
      navigate("/guest", { replace: true });
    }
  }

  // ── Gast-Token: sofort weiterleiten nach kurzer Anzeige ──────────
  useEffect(() => {
    if (phase === "guest") {
      const t = setTimeout(() => navigate("/guest", { replace: true }), 2000);
      return () => clearTimeout(t);
    }
  }, [phase]);

  // ── Walk-in Submit ────────────────────────────────────────────────
  const handleWalkin = async () => {
    const trimmed = name.trim();
    if (!trimmed) { setNameError("Bitte gib deinen Namen ein."); return; }
    if (trimmed.length < 2) { setNameError("Name muss mindestens 2 Zeichen haben."); return; }

    setSubmitting(true);
    setNameError(null);
    try {
      const guest = await doTableJoin(urlToken, trimmed);
      saveGuestSession({
        tableToken:   urlToken,
        guest_id:     guest.guest_id,
        guest_name:   guest.guest_name,
        table_number: guest.table_number,
        table_label:  guest.table_label,
        wedding_id:   guest.wedding_id,
        wedding_name: guest.wedding_name,
        event_date:   guest.event_date,
        location:     guest.location,
      });
      navigate("/guest", { replace: true });
    } catch (err) {
      setNameError("Fehler beim Einchecken. Bitte nochmal versuchen.");
      setSubmitting(false);
    }
  };

  const d = resolved?.data;

  // ── Loading ───────────────────────────────────────────────────────
  if (phase === "loading") return (
    <Page>
      <div style={{ textAlign: "center", paddingTop: 100 }}>
        <p style={S.monogram}>unsereHochzeits.app</p>
        <p style={{ ...S.meta, marginTop: 16 }}>Einladung wird geladen …</p>
      </div>
    </Page>
  );

  // ── Error ─────────────────────────────────────────────────────────
  if (phase === "error") return (
    <Page>
      <div style={S.card}>
        <p style={{ ...S.monogram, textAlign: "center" }}>✦</p>
        <h2 style={S.h2}>Ungültiger Link</h2>
        <p style={S.meta}>{error}</p>
        <p style={{ ...S.meta, fontSize: 13, marginTop: 8 }}>
          Bitte prüfe den Link in deiner Einladung oder wende dich ans Brautpaar.
        </p>
      </div>
    </Page>
  );

  // ── Gast-Token: Willkommen (kurz, dann Auto-Redirect) ────────────
  if (phase === "guest") return (
    <Page>
      <div aria-hidden style={S.bgTop} />
      <div aria-hidden style={S.bgBottom} />
      <div style={S.inner}>
        <div style={{ textAlign: "center" }}>
          <p style={S.monogram}>{makeInitials(d?.wedding_name)}</p>
          <div style={{ marginTop: 24 }}>
            <p style={S.eyebrow}>Herzlich willkommen</p>
            <h1 style={S.displayH1}>
              <em style={{ color: "var(--accent)", fontStyle: "italic" }}>{greetName(d?.guest_name, d?.nickname)}</em>
            </h1>
            <p style={{ ...S.meta, marginTop: 8 }}>
              zur Hochzeit von{" "}
              <strong style={{ fontFamily: "var(--font-display)", fontSize: "1.05rem" }}>
                {d?.wedding_name}
              </strong>
            </p>
            {d?.table_number && (
              <div style={{ marginTop: 16 }}>
                <DetailPill label="Dein Tisch" value={tableDisplay(d.table_number, d.table_label)} />
              </div>
            )}
          </div>
          <p style={{ ...S.meta, marginTop: 28, fontSize: 12 }}>Wird weitergeleitet …</p>
        </div>
      </div>
    </Page>
  );

  // ── Tisch Check-in (Gast bereits eingeloggt) ─────────────────────
  if (phase === "checkin" || phase === "done") {
    const session = loadGuestSession();
    return (
      <Page>
        <div style={S.inner}>
          <div style={{ textAlign: "center" }}>
            <p style={S.monogram}>{makeInitials(d?.wedding_name)}</p>
            <div style={{ marginTop: 24 }}>
              {phase === "done" ? (
                <>
                  <div style={{ fontSize: 40, marginBottom: 16 }}>✓</div>
                  <p style={S.eyebrow}>Eingecheckt!</p>
                  <h1 style={{ ...S.displayH1, fontSize: "2rem" }}>
                    <em style={{ color: "var(--accent)", fontStyle: "italic" }}>
                      {tableDisplay(loadGuestSession()?.table_number ?? d?.table_number, loadGuestSession()?.table_label ?? d?.table_label)}
                    </em>
                  </h1>
                  <p style={{ ...S.meta, marginTop: 8 }}>
                    Willkommen, {session?.guest_name} 🎉
                  </p>
                  <p style={{ ...S.meta, marginTop: 12, fontSize: 12 }}>Wird weitergeleitet …</p>
                </>
              ) : (
                <>
                  <p style={S.eyebrow}>Einen Moment …</p>
                  <h1 style={{ ...S.displayH1, fontSize: "1.8rem" }}>Check-in läuft</h1>
                  <p style={{ ...S.meta, marginTop: 8 }}>
                    Du wirst {tableDisplay(d?.table_number, d?.table_label)} zugewiesen.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </Page>
    );
  }

  // ── Walk-in: Name eingeben ────────────────────────────────────────
  const { table_number, table_label, wedding_name, event_date, location } = d ?? {};
  const displayLabel = tableDisplay(table_number, table_label) || table_label || "Willkommen";

  return (
    <Page>
      <div aria-hidden style={S.bgTop} />
      <div aria-hidden style={S.bgBottom} />
      <div style={S.inner}>
        <div className="wos-rise" style={{ textAlign: "center" }}>
          <p style={S.monogram}>{makeInitials(wedding_name)}</p>
        </div>
        <div className="wos-rise wos-rise-delay-1" style={{ textAlign: "center", marginTop: 24 }}>
          <p style={S.eyebrow}>Herzlich willkommen</p>
          <h1 style={S.displayH1}>
            <em style={{ color: "var(--accent)", fontStyle: "italic" }}>{displayLabel}</em>
          </h1>
          <p style={{ ...S.meta, marginTop: 8 }}>
            zur Hochzeit von{" "}
            <strong style={{ fontFamily: "var(--font-display)", fontSize: "1.05rem" }}>
              {wedding_name}
            </strong>
          </p>
        </div>
        <div className="wos-rise wos-rise-delay-2" style={{ marginTop: 20 }}>
          <div style={S.detailsRow}>
            <DetailPill label="Datum" value={formatDate(event_date)} />
            {location && <DetailPill label="Ort" value={location} />}
          </div>
        </div>
        <div className="wos-rise wos-rise-delay-2" style={{ marginTop: 24 }}>
          <div className="wos-divider"><span className="wos-divider-icon">✦</span></div>
        </div>
        <div className="wos-rise wos-rise-delay-3" style={{ marginTop: 20 }}>
          <label className="wos-label" htmlFor="guest-name-input">Wie heißt du?</label>
          <input
            ref={inputRef}
            id="guest-name-input"
            type="text"
            className="wos-input"
            placeholder="Dein Name …"
            value={name}
            onChange={(e) => { setName(e.target.value); setNameError(null); }}
            onKeyDown={(e) => e.key === "Enter" && !submitting && handleWalkin()}
            autoComplete="given-name"
            autoCorrect="off"
            maxLength={80}
            style={{ fontSize: 16 }}
          />
          {nameError && (
            <p style={{ marginTop: 8, fontSize: 13, color: "var(--accent-deep)" }}>{nameError}</p>
          )}
        </div>
        <div className="wos-rise wos-rise-delay-4" style={{ marginTop: 20 }}>
          <button
            className="wos-btn wos-btn-primary"
            onClick={handleWalkin}
            disabled={submitting || !name.trim()}
          >
            {submitting ? <><span className="wos-spinner" />Einen Moment …</> : "Zur Feier ✦"}
          </button>
          <p style={{ textAlign: "center", marginTop: 12, fontSize: 11, color: "var(--ink-subtle)" }}>
            Musik · Getränke · Fotos · Gästebuch
          </p>
        </div>
      </div>
    </Page>
  );
}

// ── Sub-Components ────────────────────────────────────────────────────
function Page({ children }) {
  return (
    <div style={S.page}>
      {children}
    </div>
  );
}

function DetailPill({ label, value }) {
  return (
    <div style={{
      flex: 1, padding: "10px 14px",
      background: "var(--surface)", border: "1px solid var(--line)",
      borderRadius: "var(--radius-md)", textAlign: "center",
    }}>
      <p style={{ margin: 0, fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--ink-subtle)", fontWeight: 500 }}>
        {label}
      </p>
      <p style={{ margin: "4px 0 0", fontFamily: "var(--font-display)", fontSize: "1rem", color: "var(--ink)" }}>
        {value}
      </p>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────
const S = {
  page:       { minHeight: "100dvh", background: "var(--bg)", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 20px", fontFamily: "var(--font-body)", color: "var(--ink)", position: "relative", overflow: "hidden" },
  inner:      { maxWidth: 420, width: "100%", position: "relative", zIndex: 1 },
  card:       { background: "var(--bg-elevated)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)", padding: "28px 24px", maxWidth: 380, width: "100%", margin: "0 auto", textAlign: "center" },
  monogram:   { fontFamily: "var(--font-display)", fontStyle: "italic", fontSize: 12, letterSpacing: "0.3em", textTransform: "uppercase", color: "var(--accent)", margin: 0 },
  eyebrow:    { fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--ink-subtle)", fontWeight: 500, margin: "0 0 8px" },
  displayH1:  { fontFamily: "var(--font-display)", fontSize: "clamp(2rem, 8vw, 2.8rem)", fontWeight: 500, lineHeight: 1.05, margin: 0 },
  h2:         { fontFamily: "var(--font-display)", fontSize: "1.6rem", fontWeight: 500, margin: "12px 0 8px" },
  meta:       { fontSize: 14, color: "var(--ink-muted)", margin: 0, lineHeight: 1.5 },
  detailsRow: { display: "flex", gap: 10 },
  bgTop:      { position: "absolute", top: "-80px", right: "-80px", width: 260, height: 260, background: "radial-gradient(circle, var(--accent-soft) 0%, transparent 70%)", opacity: 0.55, zIndex: 0 },
  bgBottom:   { position: "absolute", bottom: "-100px", left: "-100px", width: 300, height: 300, background: "radial-gradient(circle, var(--forest-soft) 0%, transparent 70%)", opacity: 0.4, zIndex: 0 },
};

// ── Helpers ───────────────────────────────────────────────────────────
function makeInitials(name) {
  if (!name) return "✦";
  const m = name.match(/([A-ZÄÖÜ])[^&]*&\s*([A-ZÄÖÜ])/i);
  if (m) return `${m[1].toUpperCase()} ✦ ${m[2].toUpperCase()}`;
  return name.trim().split(/\s+/).map((w) => w[0]).join(" ").toUpperCase();
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat("de-DE", { day: "numeric", month: "long", year: "numeric" }).format(new Date(iso));
  } catch { return iso; }
}
